import type {
  NotificationPreferenceRowDto,
  NotificationPreferencesDto,
} from '@reelplicate/api-client/model';
import {
  getGetNotificationPreferencesQueryKey,
  useGetNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@reelplicate/api-client/notifications';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { Skeleton, toast, Toggle } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';

import { SettingSelect, SettingToggle } from './controls';
import { SettingsCard, SettingsPageLayout, SettingsSection } from './settings-layout';

type Channel = 'email' | 'app';

const CHANNELS: { id: Channel; label: string }[] = [
  { id: 'email', label: 'Email' },
  { id: 'app', label: 'In app' },
];

/** What a row's email toggle adds under its description. */
function emailNote(row: NotificationPreferenceRowDto, timezone: string) {
  if (row.key === 'weekly-digest') return `Sent Mondays at 9:00, ${timezone} time.`;
  if (row.email !== null && !row.emailLive)
    return 'Email coming soon - your choice is kept.';
  return null;
}

/**
 * Figma "Settings — Notifications" (323:1920): delivery settings, then each
 * group of events with an Email and an In-app toggle. Your choices only, in
 * this workspace; toggles apply instantly. While emails are paused the email
 * toggles are disabled - the bell keeps working.
 */
export function NotificationsPage() {
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const { data: prefs } = useGetNotificationPreferences(workspaceId, {
    query: { enabled: Boolean(workspaceId) },
  });
  const update = useUpdateNotificationPreferences({
    mutation: { meta: { silent: true } },
  });
  const queryKey = getGetNotificationPreferencesQueryKey(workspaceId);

  /** Shows a change at once, saves it, and puts it back if saving fails. */
  const save = async (
    change: (prefs: NotificationPreferencesDto) => NotificationPreferencesDto,
    data: Parameters<typeof update.mutateAsync>[0]['data'],
  ) => {
    const before = queryClient.getQueryData<NotificationPreferencesDto>(queryKey);
    if (!before) return;
    queryClient.setQueryData(queryKey, change(before));
    try {
      queryClient.setQueryData(
        queryKey,
        await update.mutateAsync({ id: workspaceId, data }),
      );
      toast.success('Saved');
    } catch {
      queryClient.setQueryData(queryKey, before);
      toast.error('Couldn’t save that setting. Try again.');
    }
  };

  const setPaused = (paused: boolean) => save((p) => ({ ...p, paused }), { paused });

  const setRow = (key: string, channel: Channel, value: boolean) =>
    save(
      (p) => ({
        ...p,
        groups: p.groups.map((group) => ({
          ...group,
          rows: group.rows.map((row) =>
            row.key === key ? { ...row, [channel]: value } : row,
          ),
        })),
      }),
      { rows: [{ key, [channel]: value }] },
    );

  return (
    <SettingsPageLayout
      title="Notifications"
      description="Choose what reaches you and where. Only affects you, not your teammates."
    >
      <SettingsSection title="Delivery">
        <SettingsCard>
          <SettingToggle
            label="Pause all notifications"
            description="No emails are sent until you turn this back on. The bell in the app keeps working."
            checked={prefs?.paused ?? false}
            onCheckedChange={(paused) => void setPaused(paused)}
            disabled={!prefs}
          />
          <SettingSelect
            label="Email frequency"
            description="Coming soon: group emails so you don’t get one per event."
            defaultValue="hourly"
            disabled
            options={[
              ['instant', 'As it happens'],
              ['hourly', 'Hourly digest'],
              ['daily', 'Daily digest'],
            ]}
          />
        </SettingsCard>
      </SettingsSection>

      {!prefs ? (
        <Skeleton shape="rectangle" className="h-96" />
      ) : (
        prefs.groups.map((group) => (
          <SettingsSection
            key={group.title}
            title={group.title}
            description={group.description}
          >
            <SettingsCard>
              <div aria-hidden className="flex items-center pt-3 pb-2">
                <span className="flex-1" />
                {CHANNELS.map((channel) => (
                  <span
                    key={channel.id}
                    className="text-label-sm text-text-secondary w-22 text-center"
                  >
                    {channel.label}
                  </span>
                ))}
              </div>
              {group.rows.map((row) => {
                const note = emailNote(row, prefs.timezone);
                return (
                  <div key={row.key} className="flex items-center py-4">
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <p className="text-body-md-strong text-text-primary">{row.label}</p>
                      <p className="text-body-sm text-text-secondary">
                        {row.description}
                      </p>
                      {note && <p className="text-caption text-text-tertiary">{note}</p>}
                    </div>
                    {CHANNELS.map((channel) => {
                      const value = row[channel.id];
                      return (
                        <div
                          key={channel.id}
                          className="flex w-22 shrink-0 justify-center"
                        >
                          {value === null ? (
                            <span className="text-body-sm text-text-tertiary">
                              <span aria-hidden>—</span>
                              <span className="sr-only">
                                {row.label} isn’t available{' '}
                                {channel.id === 'email' ? 'by email' : 'in the app'}
                              </span>
                            </span>
                          ) : (
                            <Toggle
                              aria-label={`${row.label}: ${channel.label.toLowerCase()}`}
                              checked={value}
                              onCheckedChange={(next) =>
                                void setRow(row.key, channel.id, next)
                              }
                              disabled={channel.id === 'email' && prefs.paused}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </SettingsCard>
          </SettingsSection>
        ))
      )}
    </SettingsPageLayout>
  );
}
